import React, { useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Link, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { SessionContextProvider, useSession, useSessionContext, useSupabaseClient } from '@supabase/auth-helpers-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { supabase } from './supabaseClient';
import ProjectRegister from './modules/ProjectRegister';
import JobCards from './modules/JobCards';
import Timesheets from './modules/Timesheets';
import TimesheetApprovals from './modules/TimesheetApprovals';
import EmployeeManagement from './modules/EmployeeManagement';
import AllTimesheets from './modules/AllTimesheets';
import InvoiceRegister from './modules/InvoiceRegister';
import ServiceRates from './modules/ServiceRates';
import Fees from './modules/Fees';
import Budget from './modules/Budget';
import Workflows from './modules/Workflows';
import Documents from './modules/Documents';
import Contacts from './modules/Contacts';
import NonConformanceRegister from './modules/NonConformanceRegister';
import CorRegister from './modules/CorRegister';
import SkillsMatrix from './modules/SkillsMatrix';
import Login from './modules/Login';
import Signup from './modules/Signup';
import ForgotPassword from './modules/ForgotPassword';
import ResetPassword from './modules/ResetPassword';
import NotFound from './components/NotFound';
import UserAvatar from './components/UserAvatar';
import ChangePasswordModal from './components/ChangePasswordModal';
import ReadOnlyToast from './components/ReadOnlyToast';

// Create a QueryClient with sensible defaults for caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // Data considered fresh for 2 minutes
      gcTime: 10 * 60 * 1000,   // Keep cached data for 10 minutes after unmount
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 1, // Only retry failed requests once
    },
  },
});

// Home component removed - not currently used

// Profile-checking route component  
function ProfileRoute({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const [hasWaited, setHasWaited] = useState(false);

  // Wait a brief moment for session to stabilize
  useEffect(() => {
    const timer = setTimeout(() => setHasWaited(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // A session we already have needs no wait. AuthReady holds the entire router
  // until the session context has finished loading, so by the time a guard
  // renders, `session` is either a real session or a definite null -- the
  // 100ms below is only ever needed to avoid redirecting someone whose session
  // is still resolving.
  //
  // Waiting unconditionally cost a blank frame INSIDE Layout on every full page
  // load, which is what made refreshing an open document flash the app shell
  // (sidebar, page heading, empty content) before the editor appeared.
  if (session) {
    return <>{children}</>;
  }

  // Not signed in, or not yet resolved: hold briefly rather than bouncing
  // someone to /signup on a session that arrives a moment later.
  if (!hasWaited || session === undefined) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        fontSize: '0.9rem',
        color: '#999'
      }}>
        {/* Brief loading to prevent flicker */}
      </div>
    );
  }

  console.log('ProfileRoute: No session after wait, redirecting to signup from', location.pathname);
  return <Navigate to="/signup" state={{ from: location }} replace />;

}

// Incomplete Profile Route Component (for signup page - accessible to unauthenticated users or users without profiles)
function IncompleteProfileRoute({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const supabase = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  React.useEffect(() => {
    const checkProfileStatus = async () => {
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      try {
        // Check if user has a profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, team_member_id')
          .eq('id', session.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          // PGRST116 = row not found
          console.error('Profile check error:', profileError);
        }

        setHasProfile(!!profileData);
      } catch (error) {
        console.error('Profile check error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkProfileStatus();
  }, [session, supabase]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.1rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Allow unauthenticated users to access signup page
  if (!session) {
    return <>{children}</>;
  }

  if (hasProfile) {
    // User already has a profile - redirect to projects (default page)
    return <Navigate to="/projects" replace />;
  }

  return <>{children}</>;
}

// Protected Route Component (for routes that need authentication only)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();

  // While session is loading, render the children anyway to prevent route fallbacks
  if (session === undefined) {
    return <>{children}</>;
  }

  // Only redirect if definitely not authenticated
  if (session === null) {
    return <Navigate to="/signup" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Actually use ProtectedRoute to avoid unused variable warning
ProtectedRoute.displayName = 'ProtectedRoute';

// Public Route Component (for login page)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const from = location.state?.from?.pathname || '/projects';

  React.useEffect(() => {
    const checkSession = async () => {
      await supabase.auth.getSession();
      setIsLoading(false);
    };
    checkSession();
  }, []);

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (session) {
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

// Manager Route Component (for routes that require manager role)
function ManagerRoute({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const [hasWaited, setHasWaited] = useState(false);

  // Wait a brief moment for session to stabilize
  useEffect(() => {
    const timer = setTimeout(() => setHasWaited(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // A session we already have needs no wait. AuthReady holds the entire router
  // until the session context has finished loading, so by the time a guard
  // renders, `session` is either a real session or a definite null -- the
  // 100ms below is only ever needed to avoid redirecting someone whose session
  // is still resolving.
  //
  // Waiting unconditionally cost a blank frame INSIDE Layout on every full page
  // load, which is what made refreshing an open document flash the app shell
  // (sidebar, page heading, empty content) before the editor appeared.
  if (session) {
    return <>{children}</>;
  }

  // Not signed in, or not yet resolved: hold briefly rather than bouncing
  // someone to /signup on a session that arrives a moment later.
  if (!hasWaited || session === undefined) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        fontSize: '0.9rem',
        color: '#999'
      }}>
        {/* Brief loading to prevent flicker */}
      </div>
    );
  }

  console.log('ManagerRoute: No session after wait, redirecting to signup from', location.pathname);
  return <Navigate to="/signup" state={{ from: location }} replace />;

}

// Workflow types for the nav
const WORKFLOW_NAV_ITEMS = [
  { type: 'Design', path: 'design', label: 'Design' },
  { type: 'Advisory', path: 'advisory', label: 'Advisory' },
  { type: 'RWC', path: 'rwc', label: 'RWC' },
  { type: 'RWD', path: 'rwd', label: 'RWD' },
] as const;


/** Exact pathnames of the pages built on the ListPage shell (components/ListPage). */
const COMMAND_BAR_PATHS = new Set<string>(['/documents', '/projects', '/invoices', '/fees', '/job-cards', '/ncr', '/cor', '/contacts', '/budget', '/employee-management', '/skills-matrix', '/service-rates', '/timesheets', '/all-timesheets']);
/** Path prefixes of shell pages with a parameter: the four workflow boards. */
const COMMAND_BAR_PREFIXES = ['/workflows/'];
const isCommandBarPage = (pathname: string): boolean =>
  COMMAND_BAR_PATHS.has(pathname) || COMMAND_BAR_PREFIXES.some((p) => pathname.startsWith(p));

function Layout() {
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [userFirstName, setUserFirstName] = useState<string>('');
  const [userLastName, setUserLastName] = useState<string>('');
  const [isManualToggle, setIsManualToggle] = useState(false); // Track if user manually toggled
  const [isWorkflowsExpanded, setIsWorkflowsExpanded] = useState(false); // Workflows sub-nav state
  const [primaryWorkflowType, setPrimaryWorkflowType] = useState<string | null>(null); // Primary workflow preference
  const [isSavingPrimary, setIsSavingPrimary] = useState(false); // Loading state for saving preference
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const supabase = useSupabaseClient();
  // Pages built on the ListPage shell render their own title in the command
  // bar and scroll internally, so the Layout's h1 is suppressed for them. A
  // route missing from here shows two titles and a dead 56px header, and its
  // sticky table header has no bounded scroll box to stick inside. See
  // DEVELOPMENT.md "List pages: the command bar shell".
  const commandBarPage = isCommandBarPage(location.pathname);
  const isFullWidth = commandBarPage || location.pathname === '/projects' || location.pathname === '/job-cards' || location.pathname === '/invoices' || location.pathname === '/employee-management' || location.pathname === '/service-rates' || location.pathname === '/fees' || location.pathname === '/budget' || location.pathname.startsWith('/workflows') || location.pathname.startsWith('/documents') || location.pathname === '/ncr' || location.pathname === '/cor' || location.pathname === '/skills-matrix';
  const isInternalScroll = commandBarPage || location.pathname.startsWith('/workflows') || location.pathname === '/budget' || location.pathname === '/skills-matrix'; // Pages with internal column scrolling

  // Collapse the nav automatically when the window shrinks below the
  // tablet breakpoint (the nav is collapsed by default; we never auto-
  // expand on resize since the user opted in to whatever state is current).
  React.useEffect(() => {
    const handleResize = () => {
      const isTabletOrMobile = window.innerWidth <= 992; // $breakpoint-lg
      if (!isManualToggle && isTabletOrMobile) {
        setIsNavCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isManualToggle]);

  // Reset manual toggle flag after 30 seconds to allow auto-responsive behavior again
  React.useEffect(() => {
    if (isManualToggle) {
      const timer = setTimeout(() => {
        setIsManualToggle(false);
      }, 30000); // 30 seconds timeout (longer so it doesn't unexpectedly re-collapse)

      return () => clearTimeout(timer);
    }
  }, [isManualToggle]);

  // Manual toggle handler
  const handleNavToggle = () => {
    setIsNavCollapsed(!isNavCollapsed);
    setIsManualToggle(true); // Mark as manual toggle
  };

  // Track the last valid page title to prevent flickering during transitions
  const lastValidTitleRef = React.useRef('Workflows');
  
  // Get page title based on current route - memoized to prevent flickering during transitions
  const pageTitle = React.useMemo(() => {
    let title = '';
    
    // Handle workflow routes
    if (location.pathname.startsWith('/workflows/')) {
      const workflowType = location.pathname.split('/')[2];
      const typeLabels: Record<string, string> = {
        design: 'Design Workflow',
        advisory: 'Advisory Workflow',
        rwc: 'RWC Workflow',
        rwd: 'RWD Workflow'
      };
      title = typeLabels[workflowType?.toLowerCase()] || 'Workflows';
    } else if (location.pathname.startsWith('/documents')) {
      // Covers both the list (/documents) and an open document
      // (/documents/:id), which now share a single route.
      title = 'Documents';
    } else {
      switch (location.pathname) {
        case '/':
        case '/workflows':
          title = 'Workflows';
          break;
        case '/projects':
          title = 'Project Register';
          break;
        case '/job-cards':
          title = 'Job Cards';
          break;
        case '/timesheets':
          title = 'Timesheets';
          break;
        case '/timesheet-approvals':
          title = 'Timesheet Approvals';
          break;
        case '/all-timesheets':
          title = 'All Timesheets';
          break;
        case '/employee-management':
          title = 'Employee Profiles';
          break;
        case '/invoices':
          title = 'Invoice Register';
          break;
        case '/service-rates':
          title = 'Service Rates';
          break;
        case '/fees':
          title = 'Fees';
          break;
        case '/budget':
          title = 'Budget';
          break;
        case '/documents':
          title = 'Documents';
          break;
        case '/contacts':
          title = 'Contacts';
          break;
        case '/ncr':
          title = 'Non-Conformance Register';
          break;
        case '/cor':
          title = 'COR Register';
          break;
        case '/skills-matrix':
          title = 'Skills Matrix';
          break;
        default:
          break;
      }
    }
    
    // If we have a valid title, update the ref
    if (title) {
      lastValidTitleRef.current = title;
      return title;
    }
    
    // Otherwise return the last valid title to prevent flicker
    return lastValidTitleRef.current;
  }, [location.pathname]);

  // Update browser tab title when page changes
  useEffect(() => {
    document.title = `${pageTitle} | TE Admin`;
  }, [pageTitle]);

  // Check if user is a manager, get user names, and primary workflow preference
  React.useEffect(() => {
    const fetchUserData = async () => {
      if (!session?.user) return;

      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select(`
            primary_workflow_type,
            team_members (
              role,
              first_name,
              last_name,
              is_active
            )
          `)
          .eq('id', session.user.id)
          .single();

        // Set primary workflow preference
        setPrimaryWorkflowType(profileData?.primary_workflow_type || null);

        const teamMember = profileData?.team_members as any;
        // Deactivated users are refused new tokens by the auth hook, but an
        // already-issued access token stays valid until it expires — end the
        // session on the next load instead of waiting out the expiry.
        if (teamMember?.is_active === false) {
          await supabase.auth.signOut();
          return;
        }
        if (teamMember) {
          const userRole = teamMember?.role;
          // QA auditors get the same VISIBILITY as managers (read-only is enforced
          // in the DB and via disabled controls), so include them in the nav gate.
          setIsManager(userRole === 'manager' || userRole === 'qa');
          setUserFirstName(teamMember?.first_name || '');
          setUserLastName(teamMember?.last_name || '');
        }
      } catch (error) {
        console.error('User data fetch error:', error);
      }
    };

    fetchUserData();
  }, [session, supabase]);

  // Auto-expand workflows dropdown when on a workflow page
  React.useEffect(() => {
    if (location.pathname.startsWith('/workflows')) {
      setIsWorkflowsExpanded(true);
    }
  }, [location.pathname]);

  // Handle saving primary workflow preference
  const handleSetPrimaryWorkflow = async (workflowType: string) => {
    if (!session?.user || isSavingPrimary) return;

    // If clicking the current primary, unset it
    const newPrimary = primaryWorkflowType === workflowType ? null : workflowType;
    const previousPrimary = primaryWorkflowType;

    // Optimistic update
    setPrimaryWorkflowType(newPrimary);
    setIsSavingPrimary(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ primary_workflow_type: newPrimary })
        .eq('id', session.user.id);

      if (error) {
        console.error('Error saving primary workflow:', error);
        // Revert optimistic update on error
        setPrimaryWorkflowType(previousPrimary);
      } else {
        console.log('Primary workflow saved:', newPrimary);
      }
    } catch (err) {
      console.error('Error saving primary workflow:', err);
      // Revert optimistic update on error
      setPrimaryWorkflowType(previousPrimary);
    } finally {
      setIsSavingPrimary(false);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
      } else {
        navigate('/login');
      }
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="app">
      <ReadOnlyToast />
      <aside className={`left-nav ${isNavCollapsed ? 'collapsed' : ''}`}>
        <div className="nav-header">
          <div className="logo">
            <span className="logo-mark">
              <img src="/te-logo.png" alt="Terpkos Engineering" />
            </span>
            <span className="logo-wordmark">
              terpkos
              <em>engineering</em>
            </span>
          </div>
        </div>
        <nav>
          {/* CORE PAGES - Available to all users (no heading needed) */}
          <div className="nav-section">
            <ul className="nav-section-items">
              <li>
                <Link 
                  to="/projects" 
                  className={location.pathname === '/projects' ? 'active' : ''}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Projects</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/timesheets" 
                  className={location.pathname === '/timesheets' ? 'active' : ''}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Timesheets</span>
                </Link>
              </li>
              {/* Workflows with expandable sub-navigation */}
              <li className={`has-submenu ${isWorkflowsExpanded ? 'expanded' : ''} ${location.pathname.startsWith('/workflows') ? 'active-parent' : ''}`}>
                <button 
                  className={`nav-parent-button ${location.pathname.startsWith('/workflows') ? 'active' : ''}`}
                  onClick={() => {
                    // Toggle dropdown open
                    setIsWorkflowsExpanded(!isWorkflowsExpanded);
                    // If not already on a workflow page, navigate to primary workflow
                    if (!location.pathname.startsWith('/workflows')) {
                      navigate('/workflows');
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3H10V10H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 3H21V10H14V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 14H21V21H14V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 14H10V21H3V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Workflows</span>
                  <svg 
                    className={`chevron ${isWorkflowsExpanded ? 'expanded' : ''}`} 
                    width="12" 
                    height="12" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <ul className={`submenu ${isWorkflowsExpanded ? 'expanded' : ''}`}>
                  {WORKFLOW_NAV_ITEMS.map(({ type, path, label }) => (
                    <li key={path}>
                      <Link 
                        to={`/workflows/${path}`} 
                        className={location.pathname === `/workflows/${path}` ? 'active' : ''}
                      >
                        <span>{label}</span>
                        <button
                          className={`star-button ${primaryWorkflowType === type ? 'starred' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSetPrimaryWorkflow(type);
                          }}
                          title={primaryWorkflowType === type ? 'Remove as primary workflow' : 'Set as primary workflow'}
                          disabled={isSavingPrimary}
                        >
                          <svg 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill={primaryWorkflowType === type ? 'currentColor' : 'none'}
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </Link>
                    </li>
                  ))}
</ul>
                </li>
              <li>
                <Link 
                  to="/documents" 
                  className={location.pathname.startsWith('/documents') ? 'active' : ''}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Documents</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/contacts" 
                  className={location.pathname === '/contacts' ? 'active' : ''}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Contacts</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/ncr"
                  className={location.pathname === '/ncr' ? 'active' : ''}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="8" y="2" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="m9.5 11.5 5 5M14.5 11.5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>NCR Register</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* MANAGEMENT SECTION - Manager only */}
          {isManager && (
            <div className="nav-section">
              <h4 className="nav-section-title">MANAGEMENT</h4>
              <ul className="nav-section-items">
                <li>
                  <Link
                    to="/budget"
                    className={location.pathname === '/budget' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 14l4-4 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Budget</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fees"
                    className={location.pathname === '/fees' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M13.5 12.5c-.4-.6-1.2-1-2-1-1.1 0-1.8.6-1.8 1.4 0 .9.8 1.2 1.8 1.4 1 .2 1.8.5 1.8 1.4 0 .8-.7 1.4-1.8 1.4-.8 0-1.6-.4-2-1M11.8 10.3v1.2m0 4.2v-1.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Fees</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/job-cards"
                    className={location.pathname === '/job-cards' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Job Cards</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/invoices" 
                    className={location.pathname === '/invoices' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Invoices</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cor"
                    className={location.pathname === '/cor' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 3v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 21h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 7h4c2 0 5-1 7-2 1 .67 2.4 1.3 4 1.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 14l3-7 3 7c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 14l3-7 3 7c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>COR Register</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/skills-matrix"
                    className={location.pathname === '/skills-matrix' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>Skills Matrix</span>
                  </Link>
                </li>
                {/* Timesheet Approvals - Currently disabled (auto-approve on submission) */}
                {/* To re-enable: Uncomment this block AND revert the set_entries_submitted() trigger */}
                {/* <li>
                  <Link 
                    to="/timesheet-approvals" 
                    className={location.pathname === '/timesheet-approvals' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Approvals</span>
                  </Link>
                </li> */}
                <li>
                  <Link 
                    to="/all-timesheets" 
                    className={location.pathname === '/all-timesheets' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>All Timesheets</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/employee-management" 
                    className={location.pathname === '/employee-management' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="10" r="3.5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M5.5 19a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Profiles</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/service-rates"
                    className={location.pathname === '/service-rates' ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 7H6C4.89543 7 4 7.89543 4 9V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V9C20 7.89543 19.1046 7 18 7H15M9 7V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7M9 7H15M12 12H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Rates</span>
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* ANALYTICS SECTION - Future placeholder (commented out for now) */}
          {/* isManager && (
            <div className="nav-section">
              <h4 className="nav-section-title">ANALYTICS</h4>
              <ul className="nav-section-items">
                <li>
                  <Link 
                    to="/analytics/dashboard" 
                    className={location.pathname.startsWith('/analytics') ? 'active' : ''}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21H3V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 11L12 6L16 10L21 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Dashboard</span>
                  </Link>
                </li>
              </ul>
            </div>
          ) */}
        </nav>
        
        {/* Toggle button above user section */}
        <button 
          className="nav-toggle"
          onClick={handleNavToggle}
          title={isNavCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {isNavCollapsed ? '›' : '‹'}
        </button>
        
        {/* User section at bottom of sidebar */}
        <div className="nav-user">
          {session?.user?.email ? (
            <UserAvatar
              email={session.user.email}
              firstName={userFirstName}
              lastName={userLastName}
              onSignOut={handleSignOut}
              isSigningOut={isSigningOut}
              onChangePassword={() => setIsChangePasswordOpen(true)}
            />
          ) : (
            <span>Not signed in</span>
          )}
        </div>
      </aside>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <main className={`layout ${isNavCollapsed ? 'nav-collapsed' : ''}`}>
        <div className={`content ${isFullWidth ? 'full-width' : ''} ${isInternalScroll ? 'internal-scroll' : ''}`}>
          {!commandBarPage && (
            <header className="page-header">
              <div className="page-title">
                <h1>{pageTitle}</h1>
              </div>
            </header>
          )}
          <div className="page-body">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

// Index route redirect. Invite / magic-link / recovery emails land here with
// the session already established (see AuthReady), so this is where an
// invited user without a profiles row gets steered to complete signup.
function IndexRedirect() {
  const session = useSession();
  const supabase = useSupabaseClient();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  React.useEffect(() => {
    const decide = async () => {
      if (!session?.user) {
        setRedirectPath('/login');
        return;
      }
      const { error } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', session.user.id)
        .single();
      // PGRST116 = no profiles row yet: an invited user mid-signup
      setRedirectPath(error?.code === 'PGRST116' ? '/signup' : '/projects');
    };
    decide();
  }, [session, supabase]);

  if (!redirectPath) {
    return null;
  }
  return <Navigate to={redirectPath} replace />;
}

// Workflow redirect - redirects to primary workflow or Design as default
function WorkflowRedirect() {
  const session = useSession();
  const supabase = useSupabaseClient();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  React.useEffect(() => {
    const fetchPrimaryWorkflow = async () => {
      if (!session?.user) {
        // Default to Design if no session
        setRedirectPath('/workflows/design');
        return;
      }

      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('primary_workflow_type')
          .eq('id', session.user.id)
          .single();

        // Signed in but no profile yet: an invited user landing from the
        // email link — send them to complete signup.
        if (profileError?.code === 'PGRST116') {
          setRedirectPath('/signup');
          return;
        }

        const primaryType = profileData?.primary_workflow_type;
        if (primaryType) {
          // Map type to path (Design -> design, RWC -> rwc, etc.)
          const pathMap: Record<string, string> = {
            'Design': 'design',
            'Advisory': 'advisory',
            'RWC': 'rwc',
            'RWD': 'rwd'
          };
          setRedirectPath(`/workflows/${pathMap[primaryType] || 'design'}`);
        } else {
          // No primary set, default to Design
          setRedirectPath('/workflows/design');
        }
      } catch (error) {
        console.error('Error fetching primary workflow:', error);
        setRedirectPath('/workflows/design');
      }
    };

    fetchPrimaryWorkflow();
  }, [session, supabase]);

  if (!redirectPath) {
    // Loading state
    return null;
  }

  return <Navigate to={redirectPath} replace />;
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: <PublicRoute><Login /></PublicRoute>
  },
  {
    path: "/signup",
    element: <IncompleteProfileRoute><Signup /></IncompleteProfileRoute>
  },
  {
    path: "/forgot-password",
    element: <PublicRoute><ForgotPassword /></PublicRoute>
  },
  {
    // No route wrapper: the recovery email link signs the user into a
    // temporary session, and the component itself handles the no-session
    // (invalid/expired link) case.
    path: "/reset-password",
    element: <ResetPassword />
  },
  // Invoice Editor - rendered outside Layout for full-screen editing
  {
    path: "/invoices/:invoiceId",
    element: <ManagerRoute><InvoiceRegister /></ManagerRoute>
  },
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        path: "projects",
        element: <ProfileRoute><ProjectRegister /></ProfileRoute>
      },
      {
        path: "ncr",
        element: <ProfileRoute><NonConformanceRegister /></ProfileRoute>
      },
      {
        path: "cor",
        element: <ManagerRoute><CorRegister /></ManagerRoute>
      },
      {
        path: "skills-matrix",
        element: <ManagerRoute><SkillsMatrix /></ManagerRoute>
      },
      {
        path: "job-cards",
        element: <ManagerRoute><JobCards /></ManagerRoute>
      },
      {
        path: "timesheets",
        element: <ProfileRoute><Timesheets /></ProfileRoute>
      },
      {
        path: "timesheet-approvals",
        element: <ManagerRoute><TimesheetApprovals /></ManagerRoute>
      },
      {
        path: "all-timesheets",
        element: <ManagerRoute><AllTimesheets /></ManagerRoute>
      },
      {
        path: "employee-management",
        element: <ManagerRoute><EmployeeManagement /></ManagerRoute>
      },
      {
        path: "invoices",
        element: <ManagerRoute><InvoiceRegister /></ManagerRoute>
      },
      {
        path: "service-rates",
        element: <ManagerRoute><ServiceRates /></ManagerRoute>
      },
      {
        path: "fees",
        element: <ManagerRoute><Fees /></ManagerRoute>
      },
      {
        path: "budget",
        element: <ManagerRoute><Budget /></ManagerRoute>
      },
      {
        path: "workflows",
        element: <ProfileRoute><WorkflowRedirect /></ProfileRoute>
      },
      {
        path: "workflows/:workflowType",
        element: <ProfileRoute><Workflows /></ProfileRoute>
      },
      {
        path: "contacts",
        element: <ProfileRoute><Contacts /></ProfileRoute>
      },
      {
        // One route serves both the list and the full-screen editor. The
        // editor is a position:fixed overlay, so it covers the Layout chrome
        // anyway -- keeping it on the same route (rather than a separate
        // outside-Layout route) means opening or closing a document only
        // changes the :documentId param instead of tearing down and remounting
        // Layout, which was the cause of the open/close flicker.
        path: "documents/:documentId?",
        element: <ProfileRoute><Documents /></ProfileRoute>
      },
      {
        index: true,
        element: <IndexRedirect />
      },
      {
        path: "*",
        element: <NotFound />
      }
    ]
  }
], {
  basename: '/'
});

// Hold routing until the session context has finished loading — which
// includes consuming auth tokens from the URL fragment (invite / magic link /
// recovery emails land on "/" with #access_token=...). Without this gate the
// router's immediate redirects strip the fragment before the client reads it
// and the route guards make decisions against a not-yet-hydrated session, so
// the emailed link silently fails to sign the user in.
function AuthReady({ children }: { children: React.ReactNode }) {
  const { isLoading } = useSessionContext();

  if (isLoading) {
    return null;
  }
  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionContextProvider supabaseClient={supabase}>
        <AuthReady>
          <RouterProvider router={router} />
        </AuthReady>
      </SessionContextProvider>
    </QueryClientProvider>
  );
}

export default App;
// Test change for staging
